import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { S_INPUT } from "@/components/SettingsRow";
import { ApiError } from "@/api/client";
import {
  AppUser,
  createUser,
  listUsers,
  patchUser,
  setUserPassword,
} from "@/api/settings";

type Props = { isAdmin: boolean };

const ROLES = ["viewer", "editor", "admin"];

export function NutzerSection({ isAdmin }: Props) {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const [msg, setMsg] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [invUsername, setInvUsername] = useState("");
  const [invDisplay, setInvDisplay] = useState("");
  const [invPassword, setInvPassword] = useState("");
  const [invRole, setInvRole] = useState("editor");

  const [pwUserId, setPwUserId] = useState<number | null>(null);
  const [pwValue, setPwValue] = useState("");

  const patchMut = useMutation({
    mutationFn: (args: { id: number; patch: Parameters<typeof patchUser>[1] }) =>
      patchUser(args.id, args.patch),
    onSuccess: async () => {
      setMsg("Aktualisiert.");
      await qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: unknown) => setMsg(e instanceof ApiError ? e.message : "Fehler."),
  });

  const inviteMut = useMutation({
    mutationFn: () =>
      createUser({
        username: invUsername.trim(),
        display_name: invDisplay.trim(),
        password: invPassword,
        role: invRole,
        is_active: true,
      }),
    onSuccess: async () => {
      setShowInvite(false);
      setInvUsername("");
      setInvDisplay("");
      setInvPassword("");
      setInvRole("editor");
      setMsg("Benutzer angelegt.");
      await qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: unknown) => setMsg(e instanceof ApiError ? e.message : "Fehler."),
  });

  const pwMut = useMutation({
    mutationFn: () => setUserPassword(pwUserId!, pwValue),
    onSuccess: () => {
      setPwUserId(null);
      setPwValue("");
      setMsg("Passwort gesetzt.");
    },
    onError: (e: unknown) => setMsg(e instanceof ApiError ? e.message : "Fehler."),
  });

  return (
    <>
      <Card padded={false} style={{ marginBottom: 16 }}>
        <div style={{ padding: "var(--pad-card)", borderBottom: "1px solid var(--border)" }}>
          <SectionHeader
            title="Team"
            desc="Personen, die Zugriff auf das Buchungssystem haben."
            actions={
              isAdmin && (
                <Btn
                  variant="primary"
                  icon="person_add"
                  size="sm"
                  onClick={() => setShowInvite((v) => !v)}
                >
                  {showInvite ? "Abbrechen" : "Benutzer anlegen"}
                </Btn>
              )
            }
          />
        </div>
        {!isAdmin && (
          <div
            style={{
              padding: "10px var(--pad-card)",
              fontSize: 12,
              color: "var(--fg-muted)",
              background: "var(--bg-subtle)",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — nur Administratoren können Benutzer ändern.
          </div>
        )}
        {showInvite && isAdmin && (
          <div
            style={{
              padding: "var(--pad-card)",
              borderBottom: "1px solid var(--border)",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            }}
          >
            <input
              style={S_INPUT}
              placeholder="Benutzername"
              value={invUsername}
              onChange={(e) => setInvUsername(e.target.value)}
            />
            <input
              style={S_INPUT}
              placeholder="Anzeigename"
              value={invDisplay}
              onChange={(e) => setInvDisplay(e.target.value)}
            />
            <input
              style={S_INPUT}
              type="password"
              placeholder="Initiales Passwort (mind. 8 Zeichen)"
              value={invPassword}
              onChange={(e) => setInvPassword(e.target.value)}
            />
            <select
              style={S_INPUT}
              value={invRole}
              onChange={(e) => setInvRole(e.target.value)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn
                variant="primary"
                icon="check"
                size="sm"
                onClick={() => inviteMut.mutate()}
                disabled={inviteMut.isPending || !invUsername || invPassword.length < 8}
              >
                Anlegen
              </Btn>
            </div>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--bg-subtle)" }}>
              {["Person", "Rolle", "Status", "Zuletzt aktiv", "Passwort"].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "10px 14px",
                    textAlign: "left",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    color: "var(--fg-muted)",
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(usersQ.data ?? []).map((u: AppUser) => (
              <tr key={u.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600 }}>{u.display_name}</div>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>{u.username}</div>
                </td>
                <td style={{ padding: "12px 14px" }}>
                  <select
                    style={{ ...S_INPUT, maxWidth: 140 }}
                    value={u.role}
                    disabled={!isAdmin}
                    onChange={(e) => patchMut.mutate({ id: u.id, patch: { role: e.target.value } })}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: "12px 14px" }}>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      color: u.is_active ? "var(--status-confirm)" : "var(--fg-muted)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={u.is_active}
                      disabled={!isAdmin}
                      onChange={(e) =>
                        patchMut.mutate({ id: u.id, patch: { is_active: e.target.checked } })
                      }
                    />
                    {u.is_active ? "aktiv" : "deaktiviert"}
                  </label>
                </td>
                <td style={{ padding: "12px 14px", color: "var(--fg-muted)", fontSize: 12 }}>
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString("de-DE") : "—"}
                </td>
                <td style={{ padding: "12px 14px" }}>
                  {pwUserId === u.id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        style={{ ...S_INPUT, maxWidth: 180 }}
                        type="password"
                        placeholder="Neues Passwort"
                        value={pwValue}
                        onChange={(e) => setPwValue(e.target.value)}
                      />
                      <Btn
                        variant="primary"
                        icon="check"
                        size="sm"
                        onClick={() => pwMut.mutate()}
                        disabled={pwValue.length < 8 || pwMut.isPending}
                      >
                        Setzen
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPwUserId(null);
                          setPwValue("");
                        }}
                      >
                        Abbrechen
                      </Btn>
                    </div>
                  ) : (
                    <Btn
                      variant="ghost"
                      icon="lock_reset"
                      size="sm"
                      disabled={!isAdmin}
                      onClick={() => {
                        setPwUserId(u.id);
                        setPwValue("");
                      }}
                    >
                      Zurücksetzen
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
            {usersQ.isLoading && (
              <tr>
                <td colSpan={5} style={{ padding: 16, color: "var(--fg-muted)" }}>
                  Lade…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {msg && (
        <div style={{ fontSize: 12, color: "var(--fg-muted)", textAlign: "right" }}>{msg}</div>
      )}
    </>
  );
}
